// The time wheel: the legacy WheelTimePicker and MobileWheelTimePicker.
//
// Hours in one column, quarter hours in the other. On a desktop the panel
// floats under the field: an hour is taken at once, a minute takes the hour
// with it and closes, and a press anywhere else keeps what was chosen. On a
// phone it is a dialog with cancel and accept, as the legacy one was.
//
// Both lists are written three times over so that scrolling past either end
// lands on the same value again — the legacy "selection is parsed three times"
// trick, which made a short list feel like a wheel.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  DialogContent,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { at, fmtTime } from "./dates";
import { useBelowSm } from "./media";
import { ActionBar, Floating, Modal, PickerField } from "./popup";

const HOURS = Array.from({ length: 24 * 3 + 1 }, (_, i) => i % 24);
const MINUTES = Array.from({ length: 4 * 3 + 1 }, (_, i) => (i % 4) * 15);
const HOURS_MIDDLE = 24;
const MINUTES_MIDDLE = 4;

const pad2 = (n: number) => String(n).padStart(2, "0");

/** The legacy StyledOutlinedTextField, which the phone's time field used. */
export const StyledOutlinedTextField = styled(TextField)(({ theme }) => ({
  "& label.Mui-focused": {
    "& .Mui-disabled": {
      color: theme.palette.text.disabled,
    },
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: theme.palette.text.disabled,
    fontWeight: "normal",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderWidth: 1.5,
      borderColor: theme.palette.divider,
    },
    "&:hover fieldset": {
      borderWidth: 1.5,
      borderColor: theme.palette.mode === "dark" ? theme.palette.action.selected : theme.palette.text.disabled,
    },
    "&.Mui-focused fieldset": {
      borderWidth: 1.5,
      borderColor: theme.palette.primary.main,
    },
  },
  "& .MuiOutlinedInput-root.Mui-disabled": {
    backgroundColor: theme.palette.background.default,
    "&.Mui-disabled fieldset": {
      borderWidth: 1.5,
      borderColor: theme.palette.mode === "dark" ? theme.palette.action.disabledBackground : theme.palette.action.disabled,
    },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: theme.palette.text.disabled,
  },
  "& .MuiOutlinedInput-input": {
    color: theme.palette.text.primary,
    fontWeight: "normal",
  },
  "& .MuiSvgIcon-root": {
    color: theme.palette.text.primary,
  },
  "& .MuiSvgIcon-root.Mui-disabled": {
    color: theme.palette.background.default,
  },
}));

/**
 * Wheel is one scrollable column. It centres the middle copy of the chosen
 * value when it appears, and whenever the scroll reaches either end it jumps
 * back to the middle copy so that the list never runs out.
 */
function Wheel({
  values,
  middle,
  selected,
  onSelect,
  mobile,
}: {
  values: number[];
  middle: number;
  selected: number;
  onSelect: (v: number) => void;
  mobile: boolean;
}) {
  const list = useRef<HTMLUListElement>(null);
  const period = values.length - 1;
  const stride = period / 3;

  const centre = useCallback((index: number) => {
    const l = list.current;
    const item = l?.children[index] as HTMLElement | undefined;
    if (!l || !item) return;
    l.scrollTop = item.offsetTop - (l.clientHeight - item.offsetHeight) / 2;
  }, []);

  useEffect(() => {
    // The panel fades in; wait a frame so that the list has its height.
    const id = requestAnimationFrame(() => centre(middle + values.slice(0, stride).indexOf(selected)));
    return () => cancelAnimationFrame(id);
    // Only when opened: re-centring after every choice would fight the scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onScroll = () => {
    const l = list.current;
    if (!l) return;
    const first = l.children[middle] as HTMLElement | undefined;
    if (!first) return;
    if (l.scrollTop === 0) l.scrollTop = first.offsetTop;
    else if (Math.trunc(l.scrollHeight - l.scrollTop) === l.clientHeight) {
      l.scrollTop = first.offsetTop + first.offsetHeight - l.clientHeight;
    }
  };

  return (
    <List
      ref={list}
      dense={!mobile}
      onScroll={onScroll}
      sx={{
        p: 0,
        ml: mobile && values.length < 20 ? 0.5 : 0,
        overflow: "auto",
        maxHeight: mobile ? 144 : 103,
        overscrollBehavior: "none",
        position: "relative",
        "&::-webkit-scrollbar": { width: 0 },
        scrollbarWidth: "none",
      }}
    >
      {values.map((v, i) => {
        const on = v === selected;
        return (
          <ListItem key={i} sx={{ p: 0, height: mobile ? 36 : undefined, borderRadius: 6, bgcolor: on ? "primary.main" : "" }}>
            <ListItemButton
              onClick={() => {
                onSelect(v);
                centre(i < stride ? i + stride : i);
              }}
              disableRipple={mobile}
              sx={{ py: mobile ? 0.5 : 0, px: mobile ? 4 : 3, borderRadius: 6 }}
            >
              <ListItemText>
                <Typography
                  color={on ? "primary.contrastText" : "text.primary"}
                  fontSize={mobile ? "0.75rem" : 12}
                  fontWeight={mobile ? "bold" : undefined}
                >
                  {pad2(v)}
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}

/** TimeField is one time of day, on the day of `value`. */
export function TimeField({
  label,
  value,
  onChange,
  showIcon = false,
  disabled = false,
  center = false,
  haveMargin = false,
}: {
  label: string;
  value: Date;
  onChange: (time: Date) => void;
  showIcon?: boolean;
  disabled?: boolean;
  center?: boolean;
  haveMargin?: boolean;
}) {
  const mobile = useBelowSm();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);

  const draftRef = useRef(draft);
  draftRef.current = draft;

  const commit = useCallback(() => {
    setOpen(false);
    onChange(draftRef.current);
  }, [onChange]);
  const cancel = () => setOpen(false);

  const setHour = (h: number) => setDraft((d) => at(d, h, d.getMinutes()));
  const setMinute = (m: number) => {
    const next = at(draft, draft.getHours(), m);
    setDraft(next);
    if (!mobile) {
      setOpen(false);
      onChange(next);
    }
  };

  if (mobile) {
    return (
      <>
        <StyledOutlinedTextField
          label={label}
          value={fmtTime(value)}
          onClick={() => {
            if (disabled) return;
            setDraft(value);
            setOpen(true);
          }}
          inputProps={{ readOnly: true }}
          disabled={disabled}
          sx={{ ml: haveMargin ? 1.5 : 0, input: { textAlign: center ? "center" : "start" } }}
        />
        <Modal open={open} onDismiss={cancel} maxWidth="xs">
          <DialogContent>
            <Grid container sx={{ pt: 2, pb: 1, display: "flex", justifyContent: "center" }}>
              <Grid
                item
                sx={{
                  p: 1.5,
                  pr: 0.5,
                  border: 1.5,
                  borderRight: 0,
                  borderColor: "divider",
                  borderTopLeftRadius: 18,
                  borderBottomLeftRadius: 18,
                }}
              >
                <Wheel values={HOURS} middle={HOURS_MIDDLE} selected={draft.getHours()} onSelect={setHour} mobile />
              </Grid>
              <Grid
                item
                sx={{
                  p: 1.5,
                  pl: 0.5,
                  border: 1.5,
                  borderLeft: 0,
                  borderColor: "divider",
                  borderTopRightRadius: 18,
                  borderBottomRightRadius: 18,
                }}
              >
                <Wheel values={MINUTES} middle={MINUTES_MIDDLE} selected={draft.getMinutes()} onSelect={setMinute} mobile />
              </Grid>
            </Grid>
          </DialogContent>
          <ActionBar onCancel={cancel} onAccept={commit} />
        </Modal>
      </>
    );
  }

  return (
    <>
      <PickerField
        label={label}
        value={fmtTime(value)}
        focused={open}
        disabled={disabled}
        fullWidth={false}
        icon={showIcon ? <AccessTimeIcon /> : undefined}
        onOpen={(a) => {
          setAnchor(a);
          if (open) {
            commit();
            return;
          }
          setDraft(value);
          setOpen(true);
        }}
      />
      <Floating open={open} anchor={anchor} onClickAway={commit} elevation={3}>
        <Grid container sx={{ py: 2, px: 2.75 }}>
          <Grid item sx={{ pr: 1.5 }}>
            <Wheel values={HOURS} middle={HOURS_MIDDLE} selected={draft.getHours()} onSelect={setHour} mobile={false} />
          </Grid>
          <Grid item>
            <Wheel values={MINUTES} middle={MINUTES_MIDDLE} selected={draft.getMinutes()} onSelect={setMinute} mobile={false} />
          </Grid>
        </Grid>
      </Floating>
    </>
  );
}
